import { type FC, useState } from "react";
import {
  Toolbar,
  Checkbox,
  Button,
  Typography,
  Divider,
  Menu,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  ActionMenuItem,
  ToolbarProgress,
  useConfirmAction,
} from "@/shared/components";
import { useTranslation } from "react-i18next";
import {
  useUpdatePlatformPagesMutation,
  useDeletePlatformPagesMutation,
  type PlatformPagesListRow,
} from "@/partner/entities/platformPages";

interface PlatformPagesListToolbarProps {
  indeterminate: boolean;
  numSelected: number;
  nodes: PlatformPagesListRow[];
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedItems: { id: string }[];
  onCloseSelected: () => void;
}

export const PlatformPagesListToolbar: FC<PlatformPagesListToolbarProps> = ({
  indeterminate,
  numSelected,
  nodes,
  onSelectAllClick,
  selectedItems,
  onCloseSelected,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [updatePlatformPages, { loading: updateLoading }] =
    useUpdatePlatformPagesMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("platformPages.snackbar.bulkUpdated"), {
          variant: "success",
        });
      },
      onError: (error: { message: string }) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["PlatformPagesList"],
    });

  const [deletePlatformPages, { loading: deleteLoading }] =
    useDeletePlatformPagesMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("platformPages.snackbar.bulkDeleted"), {
          variant: "success",
        });
      },
      onError: (error: { message: string }) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["PlatformPagesList"],
    });

  const onPublishSelected = () => {
    updatePlatformPages({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: { publishAt: new Date().toISOString() },
      },
    });
  };

  const onUnpublishSelected = () => {
    updatePlatformPages({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: { publishAt: null },
      },
    });
  };

  const onDeleteSelected = () => {
    // До этой правки массовое удаление здесь шло вообще без вопроса.
    confirm({
      title: t("common.actions.delete"),
      text: t("common.toolbar.deleteConfirm"),
      onConfirm: () =>
        deletePlatformPages({
          variables: {
            ids: selectedItems.map((item) => item.id),
          },
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={numSelected === nodes.length}
        onChange={onSelectAllClick}
        sx={{
          marginLeft: "-16px",
          marginRight: {
            xs: 0,
            sm: "16px",
          },
        }}
      />

      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>

      <Divider
        orientation="vertical"
        variant="middle"
        sx={{
          mx: 1.25,
          height: "2em",
        }}
      />

      <Button
        color="inherit"
        onClick={handleClick}
        disabled={numSelected === 0}
      >
        {t("common.actions.actions")}
      </Button>

      <Menu
        id="platform-pages-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onPublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.publish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onUnpublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.unpublish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={deleteLoading}
          onClick={() => {
            onDeleteSelected();
            handleClose();
          }}
        >
          {t("common.actions.delete")}
        </ActionMenuItem>
      </Menu>

      <ToolbarProgress loading={updateLoading || deleteLoading} />
      {confirmDialog}
    </Toolbar>
  );
};
